// ============================================================
// SERVICE — API / Supabase Integration
// ============================================================

import axios from "axios";
import axiosClient from "../lib/axiosClient";
import { Invoice, InvoiceStatus, Supplier, DashboardSummary, InvoiceLineItem } from "../types";
import type {
  BusinessDetails,
  UpdateBusinessPayload,
  CreateInvoicePayload,
  InvoiceListParams,
  PaginatedInvoicesResponse,
  UpdateInvoicePayload,
  RecheckInvoiceResponse,
  InvoiceFileLink,
  DeleteInvoiceResponse,
  CreateInvoiceLinePayload,
  UpdateInvoiceLinePayload,
  DeleteInvoiceLineResponse,
  CreateSupplierPayload,
  UpdateSupplierPayload,
  MergeSupplierPayload,
  MergeSupplierResponse,
  MenuCostingStatePayload,
  MenuCostingStateResponse,
  ParSupplierItem,
  ParTemplateItemPayload,
  ParTemplateItem,
  ParTemplate,
  ParCheck,
  SubmitParCheckPayload,
  DeleteResponse,
  GeneratedReport,
  CreateCustomReportPayload,
  ReportDownloadLink,
  ReportLineItemExportRow,
  ReportCategoryRow,
  ReportSupplierRow,
  CustomReportResponse,
  ReportWorkspaceParams,
  ReportWorkspaceResponse,
  ReportExportPayload,
  ReportExportResponse,
  OwnerSummaryReport,
  StorageProvider,
  StorageIntegration,
  StorageIntegrationPayload,
  StorageExportDestination,
  StorageFolder,
  SupplierProduct,
} from "../types/api";
import { getApiHeaders } from "../lib/apiHeaders";

// Re-export API types so callers can import from this file or from types/api directly
export type {
  BusinessDetails,
  UpdateBusinessPayload,
  CreateInvoicePayload,
  InvoiceListParams,
  PaginatedInvoicesResponse,
  UpdateInvoicePayload,
  RecheckInvoiceResponse,
  InvoiceFileLink,
  DeleteInvoiceResponse,
  CreateInvoiceLinePayload,
  UpdateInvoiceLinePayload,
  DeleteInvoiceLineResponse,
  CreateSupplierPayload,
  UpdateSupplierPayload,
  MergeSupplierPayload,
  MergeSupplierResponse,
  MenuCostingStatePayload,
  MenuCostingStateResponse,
  ParSupplierItem,
  ParTemplateItemPayload,
  ParTemplateItem,
  ParTemplate,
  ParCheck,
  SubmitParCheckPayload,
  DeleteResponse,
  GeneratedReport,
  CreateCustomReportPayload,
  ReportDownloadLink,
  ReportLineItemExportRow,
  ReportCategoryRow,
  ReportSupplierRow,
  CustomReportResponse,
  ReportWorkspaceParams,
  ReportWorkspaceResponse,
  ReportExportFormat,
  ReportExportDataset,
  ReportExportPayload,
  ReportExportResponse,
  OwnerSummaryReport,
  StorageProvider,
  StorageIntegration,
  StorageIntegrationPayload,
  StorageExportDestination,
  StorageFolder,
} from "../types/api";

export type { ExportTemplate, DateSortStructure, TransientReportResponse, OwnerBusinessSummary, ReportDateBasis } from "../types/api";

/** @deprecated Import from '../types/api' instead */
export type DropboxFolder = StorageFolder;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

async function apiConfig(businessId?: string) {
  return { headers: await getApiHeaders({ businessId }) };
}

async function apiAuthConfig() {
  return { headers: await getApiHeaders({ authOnly: true }) };
}

async function authOnlyConfig() {
  return { headers: await getApiHeaders({ authOnly: true }) };
}

function backendError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const detail = typeof data?.detail === "string" ? data.detail : undefined;
    const message = data?.error === "Supabase request failed" && detail
      ? detail
      : data?.error || detail || error.message;
    return new Error(message);
  }

  return error instanceof Error ? error : new Error("Request failed");
}

function normalizeInvoiceStatus(status: unknown): InvoiceStatus {
  if (status === "duplicate") return "duplicate";
  if (status === "flagged") return "flagged";
  return "complete";
}

function normalizeInvoice(invoice: Invoice & { status?: unknown }): Invoice {
  return {
    ...invoice,
    status: normalizeInvoiceStatus(invoice.status),
  };
}

export async function submitBusinessRegistration(payload: {
  name: string; contactPersonName: string; address: string; industry: string; abn: string; phone: string; email: string;
}): Promise<{ id: string; status: string }> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/business-registration-requests`, payload, await authOnlyConfig());
    return response.data;
  } catch (error) { throw backendError(error); }
}

export async function fetchPriceChanges(scanDate?: string, businessId?: string): Promise<any[]> {
  const qs = scanDate ? `?scanDate=${encodeURIComponent(scanDate)}` : '';
  const response = await axiosClient.get(`${API_BASE_URL}/price-changes${qs}`, await apiConfig(businessId));
  return response.data;
}

function cleanQueryParams(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );
}

function invoiceTime(value: string | null | undefined) {
  return value ? new Date(value).getTime() : 0;
}

// Date basis for filtering: invoice_date when present, else the upload date. Mirrors
// the backend so freshly scanned invoices without an invoice_date still fall in range.
function invoiceDateBasis(invoice: Invoice): string | null {
  if (invoice.invoiceDate) return invoice.invoiceDate;
  return invoice.uploadedAt ? invoice.uploadedAt.slice(0, 10) : null;
}

function filteredInvoiceFallback(invoices: Invoice[], params: InvoiceListParams) {
  const search = params.search?.trim().toLowerCase();
  return invoices
    .filter((invoice) => {
      if (!params.dateStart) return true;
      const basis = invoiceDateBasis(invoice);
      return basis !== null && basis >= params.dateStart!;
    })
    .filter((invoice) => {
      if (!params.dateEnd) return true;
      const basis = invoiceDateBasis(invoice);
      return basis !== null && basis <= params.dateEnd!;
    })
    .filter((invoice) => !params.supplierId || params.supplierId === "all" || invoice.supplier.id === params.supplierId)
    .filter((invoice) => !params.status || params.status === "all" || invoice.status === params.status)
    .filter((invoice) => !search || (invoice.invoiceNumber ?? "").toLowerCase().includes(search));
}

function sortedInvoiceFallback(invoices: Invoice[], sort?: string) {
  return [...invoices].sort((a, b) => {
    if (sort === "invoice_date_newest") return invoiceTime(b.invoiceDate) - invoiceTime(a.invoiceDate);
    if (sort === "amount_high") return (b.totalAmount ?? 0) - (a.totalAmount ?? 0);
    if (sort === "supplier_az") return a.supplier.name.localeCompare(b.supplier.name);
    return invoiceTime(b.uploadedAt || b.receivedDate) - invoiceTime(a.uploadedAt || a.receivedDate);
  });
}

function paginateInvoiceFallback(invoices: Invoice[], params: InvoiceListParams): PaginatedInvoicesResponse {
  const page = Math.max(1, params.page ?? 1);
  const pageSize = Math.max(1, params.pageSize ?? 50);
  const filtered = sortedInvoiceFallback(filteredInvoiceFallback(invoices, params), params.sort);
  const start = (page - 1) * pageSize;

  return {
    invoices: filtered.slice(start, start + pageSize),
    total: filtered.length,
    page,
    pageSize,
    totalPages: filtered.length === 0 ? 1 : Math.ceil(filtered.length / pageSize),
  };
}

export async function fetchProcessedInvoices(businessId?: string): Promise<Invoice[]>;
export async function fetchProcessedInvoices(
  businessId: string | undefined,
  params: InvoiceListParams,
): Promise<PaginatedInvoicesResponse>;
export async function fetchProcessedInvoices(
  businessId?: string,
  params?: InvoiceListParams,
): Promise<Invoice[] | PaginatedInvoicesResponse> {
  const config = params
    ? { ...(await apiConfig(businessId)), params: cleanQueryParams(params as Record<string, unknown>) }
    : await apiConfig(businessId);
  let response;
  try {
    response = await axiosClient.get(`${API_BASE_URL}/invoices`, config);
  } catch (error) {
    if (!params || !axios.isAxiosError(error) || error.response?.status !== 404) throw error;

    const fallbackResponse = await axiosClient.get(`${API_BASE_URL}/invoices`, await apiConfig(businessId));
    const fallbackInvoices = Array.isArray(fallbackResponse.data)
      ? fallbackResponse.data
      : fallbackResponse.data.invoices ?? [];
    return paginateInvoiceFallback(fallbackInvoices.map(normalizeInvoice), params);
  }

  if (Array.isArray(response.data)) {
    const invoices = response.data.map(normalizeInvoice);
    return params ? paginateInvoiceFallback(invoices, params) : invoices;
  }

  return {
    ...response.data,
    invoices: response.data.invoices.map(normalizeInvoice),
  };
}

/**
 * Fetch a single invoice by ID.
 */
export async function fetchInvoiceById(id: string, businessId?: string): Promise<Invoice> {
  const response = await axiosClient.get(`${API_BASE_URL}/invoices/${id}`, await apiConfig(businessId));
  return normalizeInvoice(response.data);
}

export async function fetchSuppliers(businessId?: string): Promise<Supplier[]> {
  const response = await axiosClient.get(`${API_BASE_URL}/suppliers`, await apiConfig(businessId));
  return response.data;
}

export async function fetchBusinessDetails(businessId?: string): Promise<BusinessDetails> {
  const response = await axiosClient.get(`${API_BASE_URL}/business`, await apiConfig(businessId));
  return response.data;
}

export async function updateBusinessDetails(
  updates: UpdateBusinessPayload,
  businessId?: string,
): Promise<BusinessDetails> {
  try {
    const response = await axiosClient.patch(`${API_BASE_URL}/business`, updates, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateBusinessSettings(
  settings: Record<string, unknown>,
  businessId?: string,
): Promise<{ settings: Record<string, unknown> }> {
  try {
    const response = await axiosClient.patch(`${API_BASE_URL}/business/settings`, settings, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchMenuCostingState(businessId?: string): Promise<MenuCostingStateResponse> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/menu-costing`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function saveMenuCostingState(
  state: MenuCostingStatePayload,
  businessId?: string,
): Promise<MenuCostingStateResponse> {
  try {
    const response = await axiosClient.put(`${API_BASE_URL}/menu-costing`, { state }, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchReports(businessId?: string): Promise<GeneratedReport[]> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/reports`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchReportWorkspace(
  params: ReportWorkspaceParams,
  businessId?: string,
): Promise<ReportWorkspaceResponse> {
  try {
    const query = new URLSearchParams();
    query.set("startDate", params.startDate);
    query.set("endDate", params.endDate);
    if (params.dateBasis) query.set("dateBasis", params.dateBasis);
    if (params.includeDuplicates) query.set("includeDuplicates", "true");
    const response = await axiosClient.get(`${API_BASE_URL}/reports/workspace?${query.toString()}`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function createCustomReport(
  payload: CreateCustomReportPayload,
  businessId?: string,
): Promise<CustomReportResponse> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/reports/custom`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function createReportExport(
  payload: ReportExportPayload,
  businessId?: string,
): Promise<ReportExportResponse> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/reports/export`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchReportDownloadLink(
  id: string,
  format: "excel" | "pdf",
  businessId?: string,
): Promise<ReportDownloadLink> {
  try {
    const response = await axiosClient.get(
      `${API_BASE_URL}/reports/${id}/download?format=${format}`,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function deleteGeneratedReport(id: string, businessId?: string): Promise<DeleteResponse> {
  try {
    const response = await axiosClient.delete(`${API_BASE_URL}/reports/${id}`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchOwnerSummaryReport(params?: {
  startDate?: string;
  endDate?: string;
  businessIds?: string[];
}): Promise<OwnerSummaryReport> {
  try {
    const query = new URLSearchParams();
    if (params?.startDate) query.set("startDate", params.startDate);
    if (params?.endDate) query.set("endDate", params.endDate);
    if (params?.businessIds?.length) query.set("businessIds", params.businessIds.join(","));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const response = await axiosClient.get(`${API_BASE_URL}/owner/summary-report${suffix}`, await apiAuthConfig());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchParTemplate(businessId?: string): Promise<ParTemplate> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/par/template`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function createParTemplateItem(payload: ParTemplateItemPayload, businessId?: string): Promise<ParTemplateItem> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/par/template/items`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchParSupplierItems(supplierId: string, businessId?: string): Promise<ParSupplierItem[]> {
  if (!supplierId) return [];
  try {
    const response = await axiosClient.get(
      `${API_BASE_URL}/par/supplier-items?supplierId=${encodeURIComponent(supplierId)}`,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateParTemplateItem(
  id: string,
  payload: Partial<ParTemplateItemPayload>,
  businessId?: string,
): Promise<ParTemplateItem> {
  try {
    const response = await axiosClient.patch(`${API_BASE_URL}/par/template/items/${id}`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function deleteParTemplateItem(id: string, businessId?: string): Promise<DeleteResponse> {
  try {
    const response = await axiosClient.delete(`${API_BASE_URL}/par/template/items/${id}`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchParChecks(businessId?: string): Promise<ParCheck[]> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/par/checks`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function submitParCheck(
  payload: SubmitParCheckPayload,
  businessId?: string,
): Promise<ParCheck> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/par/checks`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function deleteParCheck(id: string, businessId?: string): Promise<DeleteResponse> {
  try {
    const response = await axiosClient.delete(`${API_BASE_URL}/par/checks/${id}`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

/**
 * Fetch a short-lived private link for the original invoice file.
 */
export async function fetchOriginalInvoiceFile(id: string, businessId?: string): Promise<InvoiceFileLink> {
  const response = await axiosClient.get(`${API_BASE_URL}/invoices/${id}/original-file`, await apiConfig(businessId));
  return response.data;
}

/**
 * Download the original files for multiple invoices as a single .zip.
 * Returns the zip blob plus the server-suggested filename.
 */
export async function downloadInvoicesBatch(
  ids: string[],
  businessId?: string,
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/invoices/batch-download`,
      { ids },
      { ...(await apiConfig(businessId)), responseType: "blob" },
    );
    const disposition = String(response.headers["content-disposition"] || "");
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || `invoices-${new Date().toISOString().slice(0, 10)}.zip`;
    return { blob: response.data, filename };
  } catch (error) {
    // A failed request returns the JSON error as a Blob because of responseType.
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      let message = "Could not download the selected invoices.";
      try { message = JSON.parse(text).error || message; } catch { /* keep default */ }
      throw new Error(message);
    }
    throw backendError(error);
  }
}

/**
 * Update invoice status (e.g., mark alarm resolved, approve payment).
 */
export async function updateInvoiceStatus(
  id: string,
  updates: Partial<Invoice>,
  businessId?: string,
): Promise<Invoice> {
  const response = await axiosClient.patch(`${API_BASE_URL}/invoices/${id}`, updates, await apiConfig(businessId));
  return normalizeInvoice(response.data);
}

export async function updateInvoiceFields(
  id: string,
  updates: UpdateInvoicePayload,
  businessId?: string,
): Promise<Invoice> {
  const response = await axiosClient.patch(`${API_BASE_URL}/invoices/${id}`, updates, await apiConfig(businessId));
  return normalizeInvoice(response.data);
}

export async function createInvoice(
  payload: CreateInvoicePayload,
  businessId?: string,
): Promise<Invoice> {
  const response = await axiosClient.post(`${API_BASE_URL}/invoices`, payload, await apiConfig(businessId));
  return normalizeInvoice(response.data);
}

export async function resolveDuplicate(
  id: string,
  action: "delete" | "overwrite",
  businessId?: string,
): Promise<{ success: boolean; originalId?: string }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/invoices/${id}/resolve-duplicate`,
      { action },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) { throw backendError(error); }
}

export async function dismissDuplicateFile(
  id: string,
  businessId?: string,
): Promise<{ success: boolean }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/invoices/${id}/dismiss-duplicate-file`,
      {},
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) { throw backendError(error); }
}

export async function recheckInvoice(id: string, businessId?: string): Promise<RecheckInvoiceResponse> {
  const response = await axiosClient.post(`${API_BASE_URL}/invoices/${id}/recheck`, {}, await apiConfig(businessId));
  return {
    ...response.data,
    invoice: normalizeInvoice(response.data.invoice),
  };
}

export async function updateInvoiceLineFields(
  id: string,
  updates: UpdateInvoiceLinePayload,
  businessId?: string,
): Promise<InvoiceLineItem> {
  const response = await axiosClient.patch(`${API_BASE_URL}/invoice-lines/${id}`, updates, await apiConfig(businessId));
  return response.data;
}

export async function createInvoiceLine(
  payload: CreateInvoiceLinePayload,
  businessId?: string,
): Promise<InvoiceLineItem> {
  const response = await axiosClient.post(`${API_BASE_URL}/invoice-lines`, payload, await apiConfig(businessId));
  return response.data;
}

export async function deleteInvoice(id: string, businessId?: string): Promise<DeleteInvoiceResponse> {
  const response = await axiosClient.delete(`${API_BASE_URL}/invoices/${id}`, await apiConfig(businessId));
  return response.data;
}

export async function deleteInvoiceLine(id: string, businessId?: string): Promise<DeleteInvoiceLineResponse> {
  const response = await axiosClient.delete(`${API_BASE_URL}/invoice-lines/${id}`, await apiConfig(businessId));
  return response.data;
}

export async function updateSupplierFields(
  id: string,
  updates: UpdateSupplierPayload,
  businessId?: string,
): Promise<Supplier> {
  try {
    const response = await axiosClient.patch(`${API_BASE_URL}/suppliers/${id}`, updates, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function mergeSuppliers(
  payload: MergeSupplierPayload,
  businessId?: string,
): Promise<MergeSupplierResponse> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/suppliers/merge`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function createSupplier(
  payload: CreateSupplierPayload,
  businessId?: string,
): Promise<Supplier> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/suppliers`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateInvoiceLineCategory(
  lineIds: string[],
  category: string,
  businessId?: string,
): Promise<InvoiceLineItem[]> {
  try {
    const response = await axiosClient.patch(
      `${API_BASE_URL}/invoice-lines/category`,
      { lineIds, category },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateSupplierInvoiceLineCategory(
  supplierId: string,
  category: string,
  businessId?: string,
): Promise<{ updatedCount: number }> {
  try {
    const response = await axiosClient.patch(
      `${API_BASE_URL}/suppliers/${supplierId}/invoice-lines/category`,
      { category },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

/**
 * Resolve an alarm on an invoice.
 */
export async function resolveAlarm(invoiceId: string, alarmId: string): Promise<void> {
  await axiosClient.patch(`${API_BASE_URL}/invoices/${invoiceId}/alarms/${alarmId}/resolve`);
}

/**
 * Fetch dashboard summary data from the backend.
 */
export async function fetchDashboardSummary(businessId?: string): Promise<DashboardSummary> {
  const response = await axiosClient.get(`${API_BASE_URL}/dashboard/summary`, await apiConfig(businessId));
  return response.data;
}

// ── Storage Integrations ─────────────────────────────────────────────

export async function fetchStorageIntegrations(businessId?: string): Promise<StorageIntegration[]> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/integrations/storage`, await apiConfig(businessId));
    if (Array.isArray(response.data)) return response.data;
    if (response.data) return [response.data];
    return [];
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchStorageIntegration(businessId?: string): Promise<StorageIntegration | null> {
  const integrations = await fetchStorageIntegrations(businessId);
  return integrations[0] ?? null;
}

export async function connectStorageIntegration(
  payload: StorageIntegrationPayload,
  businessId?: string,
): Promise<StorageIntegration> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/integrations/storage`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateStorageIntegration(
  provider: StorageProvider,
  updates: Partial<Omit<StorageIntegrationPayload, "provider">>,
  businessId?: string,
): Promise<StorageIntegration> {
  try {
    const response = await axiosClient.patch(
      `${API_BASE_URL}/integrations/storage`,
      { ...updates, provider },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function disconnectStorageIntegration(
  provider: StorageProvider,
  businessId?: string,
): Promise<{ success: boolean }> {
  try {
    const config = await apiConfig(businessId);
    const response = await axiosClient.delete(
      `${API_BASE_URL}/integrations/storage`,
      { ...config, params: { provider } },
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function getDropboxAuthUrl(businessId?: string): Promise<{ url: string }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/integrations/storage/auth-url/dropbox`,
      {},
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function listDropboxFolders(
  businessId?: string,
  path: string = "",
): Promise<StorageFolder[]> {
  try {
    const config = await apiConfig(businessId);
    const response = await axiosClient.get(
      `${API_BASE_URL}/integrations/storage/dropbox/folders`,
      { ...config, params: { path } },
    );
    return response.data.folders;
  } catch (error) {
    throw backendError(error);
  }
}

export async function getGoogleAuthUrl(businessId?: string): Promise<{ url: string }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/integrations/storage/auth-url/google`,
      {},
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function listGoogleFolders(
  businessId?: string,
  folderId: string = "root",
): Promise<StorageFolder[]> {
  try {
    const config = await apiConfig(businessId);
    const response = await axiosClient.get(
      `${API_BASE_URL}/integrations/storage/google/folders`,
      { ...config, params: { folderId } },
    );
    return response.data.folders;
  } catch (error) {
    throw backendError(error);
  }
}

export async function getOneDriveAuthUrl(businessId?: string): Promise<{ url: string }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/integrations/storage/auth-url/onedrive`,
      {},
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function listOneDriveFolders(
  businessId?: string,
  folderId: string = "root",
): Promise<StorageFolder[]> {
  try {
    const config = await apiConfig(businessId);
    const response = await axiosClient.get(
      `${API_BASE_URL}/integrations/storage/onedrive/folders`,
      { ...config, params: { folderId } },
    );
    return response.data.folders;
  } catch (error) {
    throw backendError(error);
  }
}

export async function exportInvoiceToStorage(
  invoiceId: string,
  providers: string[],
  businessId?: string,
): Promise<StorageExportDestination[]> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/invoices/${invoiceId}/export-to-storage`,
      { providers },
      await apiConfig(businessId),
    );
    return response.data.destinations;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAllSupplierProducts(
  businessId?: string,
  filters?: { status?: string; tag?: string; supplierId?: string },
): Promise<(SupplierProduct & { supplierName: string | null })[]> {
  try {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.tag) params.set("tag", filters.tag);
    if (filters?.supplierId) params.set("supplierId", filters.supplierId);
    const qs = params.toString();
    const response = await axiosClient.get(
      `${API_BASE_URL}/suppliers/products${qs ? `?${qs}` : ""}`,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchSupplierProducts(
  supplierId: string,
  businessId?: string,
): Promise<SupplierProduct[]> {
  try {
    const response = await axiosClient.get(
      `${API_BASE_URL}/suppliers/${supplierId}/products`,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function deleteSupplierProduct(
  supplierId: string,
  productId: string,
  businessId?: string,
): Promise<{ success: boolean; deletedId: string }> {
  try {
    const response = await axiosClient.delete(
      `${API_BASE_URL}/suppliers/${supplierId}/products/${productId}`,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function mergeSupplierProduct(
  supplierId: string,
  productId: string,
  mergeIntoProductId: string,
  businessId?: string,
): Promise<{ success: boolean; target: SupplierProduct; mergedFromCode: string }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/suppliers/${supplierId}/products/${productId}/merge`,
      { mergeIntoProductId },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function resolveSupplierProduct(
  supplierId: string,
  productId: string,
  action: "confirm" | "dismiss",
  options?: { description?: string; tags?: string[] },
  businessId?: string,
): Promise<SupplierProduct> {
  try {
    const response = await axiosClient.patch(
      `${API_BASE_URL}/suppliers/${supplierId}/products/${productId}`,
      { action, ...options },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function resolveAllPending(
  businessId?: string,
): Promise<{ resolved: number; accepted: number; leftForHuman: number }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/suppliers/resolve-pending`,
      {},
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

// ── Ingredients (menu-cost ingredient layer) ──────────────────────────────────

export interface PackUnit {
  unitKey: string;
  packQuantity: number | null;
  packUnit: string | null;
}

export interface PendingUnit {
  unitKey: string;
  prevUnitKey: string | null;
  latestUnitPrice: number | null;
  latestDate: string | null;
  count: number;
}

export interface IngredientSource {
  supplierProductId: string;
  supplierId: string;
  productCode: string;
  description: string | null;
  packQuantity: number | null;
  packUnit: string | null;
  packUnits: PackUnit[];
  pendingUnits: PendingUnit[];
  observedUnits: string[];
  latestPrice: number | null;
  latestInvoicePrice: number | null;
  latestDate: string | null;
  isPreferred: boolean;
}

export interface Ingredient {
  id: string;
  name: string;
  canonicalUnit: string;
  category: string | null;
  confirmed: boolean;
  pricePolicy: string;
  preferredSupplierProductId: string | null;
  currentPrice: number | null;
  previousPrice: number | null;
  currentSupplierId: string | null;
  manualPrice: number | null;
  sourceCount: number;
  sources: IngredientSource[];
}

export interface IngredientUpdate {
  name?: string;
  canonicalUnit?: string;
  category?: string | null;
  pricePolicy?: string;
  preferredSupplierProductId?: string | null;
  confirmed?: boolean;
  manualPrice?: number | null;
}

export async function fetchIngredients(businessId?: string): Promise<Ingredient[]> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/ingredients`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export interface UnmappedSupplierProduct {
  supplierProductId: string;
  supplierId: string;
  supplierName: string | null;
  productCode: string;
  description: string | null;
  packQuantity: number | null;
  packUnit: string | null;
}

export async function fetchUnmappedSupplierProducts(businessId?: string): Promise<UnmappedSupplierProduct[]> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/ingredients/unmapped-products`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export interface IngredientPricePoint {
  supplierProductId: string;
  supplierId: string;
  date: string;
  price: number;
  flagged?: boolean;
}

export async function fetchIngredientHistory(id: string, businessId?: string): Promise<IngredientPricePoint[]> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/ingredients/${id}/history`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function createIngredient(
  payload: { name: string; canonicalUnit?: string; category?: string | null },
  businessId?: string,
): Promise<unknown> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/ingredients`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateIngredient(
  id: string,
  updates: IngredientUpdate,
  businessId?: string,
): Promise<unknown> {
  try {
    const response = await axiosClient.patch(`${API_BASE_URL}/ingredients/${id}`, updates, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function deleteIngredient(id: string, businessId?: string): Promise<DeleteResponse> {
  try {
    const response = await axiosClient.delete(`${API_BASE_URL}/ingredients/${id}`, await apiConfig(businessId));
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function attachIngredientSource(
  ingredientId: string,
  supplierProductId: string,
  businessId?: string,
): Promise<{ success: boolean; garbageCollectedIngredientId: string | null }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/ingredients/${ingredientId}/sources`,
      { supplierProductId },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function detachIngredientSource(
  ingredientId: string,
  supplierProductId: string,
  businessId?: string,
): Promise<{ success: boolean }> {
  try {
    const response = await axiosClient.delete(
      `${API_BASE_URL}/ingredients/${ingredientId}/sources/${supplierProductId}`,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateIngredientSourcePack(
  ingredientId: string,
  supplierProductId: string,
  pack: { packQuantity: number | null; packUnit: string | null; unitKey?: string },
  businessId?: string,
): Promise<{ success: boolean }> {
  try {
    const response = await axiosClient.patch(
      `${API_BASE_URL}/ingredients/${ingredientId}/sources/${supplierProductId}`,
      pack,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function resolveUnitChange(
  ingredientId: string,
  supplierProductId: string,
  payload: { unitKey: string; action: "map" | "accept"; packQuantity?: number; packUnit?: string },
  businessId?: string,
): Promise<{ success: boolean; cleared: number }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/ingredients/${ingredientId}/sources/${supplierProductId}/resolve-unit`,
      payload,
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function mergeIngredients(
  id: string,
  mergeIntoId: string,
  businessId?: string,
): Promise<{ success: boolean; mergedInto: string }> {
  try {
    const response = await axiosClient.post(
      `${API_BASE_URL}/ingredients/${id}/merge`,
      { mergeIntoId },
      await apiConfig(businessId),
    );
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

// ── Recipes (unified sub_recipe + menu_item) ──────────────────────────────────

export interface RecipeComponent {
  id: string;
  recipeId: string;
  refId: string;
  refType: "ingredient" | "recipe";
  name: string;
  quantity: number;
  unit: string;
  sourceUnit: string;
  sortOrder: number;
}

export interface Recipe {
  id: string;
  businessId: string;
  name: string;
  recipeType: "sub_recipe" | "menu_item";
  category: string | null;
  yieldQty: number;
  yieldUnit: string;
  sellingPrice: number | null;
  targetFoodCostPct: number;
  notes: string;
  createdAt: string;
  components: RecipeComponent[];
}

export interface RecipePayload {
  name: string;
  recipeType: "sub_recipe" | "menu_item";
  category?: string | null;
  yieldQty?: number;
  yieldUnit?: string;
  sellingPrice?: number | null;
  targetFoodCostPct?: number;
  notes?: string;
  components: Omit<RecipeComponent, "id" | "recipeId" | "sortOrder">[];
}

export async function fetchRecipes(businessId?: string): Promise<Recipe[]> {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/recipes`, await apiConfig(businessId));
    return response.data;
  } catch (error) { throw backendError(error); }
}

export async function createRecipe(payload: RecipePayload, businessId?: string): Promise<Recipe> {
  try {
    const response = await axiosClient.post(`${API_BASE_URL}/recipes`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) { throw backendError(error); }
}

export async function updateRecipe(id: string, payload: Partial<RecipePayload>, businessId?: string): Promise<Recipe> {
  try {
    const response = await axiosClient.patch(`${API_BASE_URL}/recipes/${id}`, payload, await apiConfig(businessId));
    return response.data;
  } catch (error) { throw backendError(error); }
}

export async function deleteRecipe(id: string, businessId?: string): Promise<{ success: boolean }> {
  try {
    const response = await axiosClient.delete(`${API_BASE_URL}/recipes/${id}`, await apiConfig(businessId));
    return response.data;
  } catch (error) { throw backendError(error); }
}
